import { useState } from 'react';
import { DiceDictionary } from '../Contexts/ConstantsCollection.js';
import { useAction } from '../Contexts/CharacterActions.js';
import { useCharacter } from '../Contexts/CharacterInfo.js';
import theme from '../../../theme.js';

export const useGearActions = ({ gearItem, isSideWeapon, isEquipment }) => {
  const { triggerRollDiceTrait, triggerRollTrait, triggerRollDiceSides } =
    useAction();
  const {
    updatePrimaryWeaponId,
    updateSecondaryWeaponId,
    updateEquipmentId,
    removeGearFromStorage,
    updateWeaponBonus,
    saveCharacter,
    updateCurrentHealth,
    updateCurrentResilience,
    updateGearUses,
    character,
  } = useCharacter();

  const [attackDisabled, setAttackDisabled] = useState(false);
  const [isConsumed, setIsConsumed] = useState(false);
  const [isThrownAway, setIsThrownAway] = useState(false);
  const [attackMessage, setAttackMessage] = useState('');
  const [attackMessageStyle, setAttackMessageStyle] = useState({
    fontSize: '3xl',
    color: 'black',
    sx: { WebkitTextStroke: '1px black' },
  });

  const removeGearAnimation = consumed => {
    if (consumed) {
      setIsConsumed(true);
    } else {
      setIsThrownAway(true);
    }
    setTimeout(() => handleThrowAway(true), 2100);
  };

  const handleThrowAway = consumed => {
    if (isSideWeapon) {
      updateSecondaryWeaponId('');
    } else if (isEquipment) {
      updateEquipmentId('');
    } else {
      updatePrimaryWeaponId('');
    }
    removeGearFromStorage(gearItem.id);
  };

  const handleStore = () => {
    updateWeaponBonus(gearItem.id, 0);
    if (isSideWeapon) {
      updateSecondaryWeaponId('');
    } else if (isEquipment) {
      updateEquipmentId('');
    } else {
      updatePrimaryWeaponId('');
    }
    saveCharacter();
  };

  const rollDamage = () => {
    var total = 0;
    var totalMsg = '';
    const traitRoll = triggerRollDiceTrait(gearItem.trait.name);
    totalMsg += traitRoll;
    total += traitRoll;
    gearItem.additionalDamage.forEach(([count, die]) => {
      const roll = triggerRollDiceSides(count, DiceDictionary[die][0]);
      totalMsg += ` + ${roll}`;
      total += roll;
    });

    if (gearItem.bonus > 0) {
      totalMsg += ` + ${gearItem.bonus}`;
      total += gearItem.bonus;
    }

    var timeout = 0;
    if (totalMsg !== total.toString()) {
      setAttackMessageStyle({
        fontSize: '2xl',
        color: theme.colors.black,
      });
      setAttackMessage(totalMsg);
      timeout = 1200;
    }

    setTimeout(
      () =>
        setAttackMessageStyle({
          fontSize: '4xl',
          color: gearItem.isEquipment
            ? theme.colors.leftSideDark
            : theme.colors.rightSideDark,
          sx: { WebkitTextStroke: '1px black' },
        }),
      timeout
    );
    setTimeout(() => setAttackMessage(total), timeout);
    setTimeout(() => setAttackMessage(null), timeout + 1200);

    return total;
  };

  const consumeUseAfterRoll = waitMs => {
    setTimeout(() => {
      if (gearItem.totalUses !== null && gearItem.totalUses > 0) {
        updateGearUses(gearItem.id, gearItem.remainingUses - 1);
        if (
          gearItem.remainingUses !== null &&
          gearItem.remainingUses - 1 === 0
        ) {
          removeGearAnimation(true);
        }
      }
    }, waitMs + 300);
  };

  const handleEquipmentRoll = () => {
    var waitMs = 1200;
    const addDmg = gearItem.additionalDamage.length > 0;
    if (gearItem.isHealthItem || gearItem.isRepairItem) {
      var roll = rollDamage();
      waitMs += 1200;
      if (gearItem.isHealthItem) {
        var newHealth = Math.min(
          character.currentHealth + roll,
          character.totalHealth
        );
        updateCurrentHealth(newHealth);
      } else {
        var newResilience = Math.min(
          character.currentResilience + roll,
          character.totalResilience
        );
        updateCurrentResilience(newResilience);
      }
    } else {
      const success = triggerRollTrait(gearItem.trait.name);
      if (success) {
        if (addDmg) {
          rollDamage();
          waitMs += 1200;
        } else {
          setAttackMessageStyle({
            fontSize: '4xl',
            color: theme.colors.lightGreen,
            sx: { WebkitTextStroke: '1px black' },
          });
          setAttackMessage('Passed');
          setTimeout(() => setAttackMessage(null), 1200);
        }
      } else {
        setAttackMessageStyle({
          fontSize: '4xl',
          color: theme.colors.redDark,
          sx: { WebkitTextStroke: '1px black' },
        });
        setAttackMessage('Failed');
        setTimeout(() => setAttackMessage(null), 1200);
      }
    }

    consumeUseAfterRoll(waitMs);
  };

  const handleWeaponRoll = () => {
    const success = triggerRollTrait(gearItem.trait.name);
    if (success) {
      rollDamage();
    } else {
      setAttackMessageStyle({
        fontSize: '4xl',
        color: theme.colors.redDark,
        sx: { WebkitTextStroke: '1px black' },
      });
      setAttackMessage('Attack Failed');
      setTimeout(() => setAttackMessage(null), 1200);
      if (gearItem.id === character.primaryWeaponId)
        updateWeaponBonus(gearItem.id, gearItem.bonus + 1);
    }
  };

  const handleAttack = () => {
    setAttackDisabled(true);
    setTimeout(() => setAttackDisabled(false), 4100);
    if (gearItem.isEquipment) {
      handleEquipmentRoll();
    } else {
      handleWeaponRoll();
    }
  };

  return {
    attackDisabled,
    isConsumed,
    isThrownAway,
    attackMessage,
    attackMessageStyle,
    handleAttack,
    handleThrowAway,
    handleStore,
    removeGearAnimation,
  };
};
